import * as React from 'react'
import { FormattedMessage } from 'react-intl'
import Button from '@material-ui/core/Button'
import Card from '@material-ui/core/Card'
import CardHeader from '@material-ui/core/CardHeader'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import DialogTitle from '@material-ui/core/DialogTitle'
import Divider from '@material-ui/core/Divider'
import Grid from '@material-ui/core/Grid'
import withStyles, { WithStyles } from '@material-ui/core/styles/withStyles'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import Typography from '@material-ui/core/Typography'
import TextField from '@material-ui/core/TextField'
import ScheduleIcon from '@material-ui/icons/Schedule'
import TodayIcon from '@material-ui/icons/Today'
import ListItem from '@material-ui/core/ListItem'
import ListItemText from '@material-ui/core/ListItemText'

import { post } from "../../config";
import { InjectAccountType, PermissionGuard } from '../atoms'

import { Session, SessionStatus, AccountType } from '../../api'
import testingHooks from '../../testing-hooks'
import { memoize } from '../../utils'
import { DateTimePicker } from '../atoms'
import loadable from '../hocs/loadable'
import withToggle, { InjectedToggleProps } from '../hocs/withToggle'
import SessionTableRow from './SessionTableRow'
import { isAdmin } from 'src/project-utils/permissions'

export interface SessionsProps {
  sessions: Session[],
  programId,
  processId,
  programUpdate,
  getProgram,
  processes,
  program,
  update,
  refreshSessions: () => void
}

interface State {
  filter: SessionStatus | undefined
  selectedSession: Session | undefined
  editingSession: boolean
  goals: string
  achievements: string
  date: number
}

const styles = {
  headerContent: {
    width: '100%',
  },
  btnPlus: {
    padding: '0 10px'
  }
}

type Props =
  SessionsProps
  & InjectedToggleProps
  & WithStyles<typeof styles>



class Sessions extends React.Component<Props, State> {
  public state = {
    filter: undefined,
    selectedSession: undefined,
    editingSession: false,
    goals: '',
    achievements: '',
    date: 0
  }

  public render() {
    return (
      <>
        <Card>
          {this.renderHeader()}
          <Divider />
          <div style={{ width: '100%', overflowX: 'auto' }}>
            {this.renderTable()}
          </div>
        </Card>
        {this.state.editingSession ? this.renderSessionEdit() : this.renderSessionSummary()}
      </>
    )
  }

  private renderHeader = () => {
    const phrasing = {
      allSessions: <FormattedMessage id={'generic.allSessions'} />,
      all: <FormattedMessage id={'dictionary.all'} />,
      completed: <FormattedMessage id={'dictionary.completed'} />,
      scheduled: <FormattedMessage id={'dictionary.scheduled'} />,
      tba: <FormattedMessage id={'dictionary.tba'} />,
    }

    const { filter } = this.state
    const { classes: { headerContent } } = this.props

    return (
      <CardHeader
        classes={{ content: headerContent }}
        title={(
          <Grid
            container
            spacing={16}
            justify={'space-between'}
          >
            <Grid
              item
              xs={12}
              md={'auto'}
            >
              {phrasing.allSessions}
              <PermissionGuard check={isAdmin}>
                <span onClick={() => this.updateNumSessions()} style={{ paddingLeft: 15, fontSize: 24, color: '#0097a7', cursor: 'pointer' }}>
                  +
                </span>  
              </PermissionGuard>
            </Grid>
            <Grid
              item
              xs={12}
              md={'auto'}
            >
              <div className={'horizontal-scroll'}>
                <div style={{ padding: 4 }}>
                  <Grid
                    container
                    spacing={8}
                    wrap={'nowrap'}
                  >
                    <Grid item>
                      <Button
                        size={'small'}
                        color={'secondary'}
                        variant={filter === undefined && 'raised' || undefined}
                        onClick={this.removeFilter}
                        data-test-id={testingHooks.buttons.sessionFilter && testingHooks.buttons.sessionFilter.all}
                        style={{
                          textTransform: 'capitalize'
                        }}
                      >
                        {phrasing.all}
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        size={'small'}
                        color={'secondary'}
                        variant={this.determineButtonVariant(SessionStatus.Done)}
                        onClick={this.memoizeSetFilter(SessionStatus.Done)}
                        data-test-id={testingHooks.buttons.sessionFilter && testingHooks.buttons.sessionFilter.completed}
                        style={{
                          textTransform: 'capitalize'
                        }}
                      >
                        {phrasing.completed}
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        size={'small'}
                        color={'secondary'}
                        variant={this.determineButtonVariant(SessionStatus.Scheduled)}
                        onClick={this.memoizeSetFilter(SessionStatus.Scheduled)}
                        data-test-id={testingHooks.buttons.sessionFilter && testingHooks.buttons.sessionFilter.scheduled}
                        style={{
                          textTransform: 'capitalize'
                        }}
                      >
                        {phrasing.scheduled}
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        size={'small'}
                        color={'secondary'}
                        variant={this.determineButtonVariant(SessionStatus.Pending)}
                        onClick={this.memoizeSetFilter(SessionStatus.Pending)}
                        data-test-id={testingHooks.buttons.sessionFilter && testingHooks.buttons.sessionFilter.tba}
                        style={{
                          textTransform: 'capitalize'
                        }}
                      >
                        {phrasing.tba}
                      </Button>
                    </Grid>
                  </Grid>
                </div>
              </div>
            </Grid>
          </Grid>
        )}
      />
    )
  }

  private renderTable = () => {
    const phrasing = {
      status: <FormattedMessage id={'dictionary.status'} />,
      date: <FormattedMessage id={'dictionary.date'} />,
      hour: <FormattedMessage id={'dictionary.hour'} />,
    }

    return (
      <Table style={{ minWidth: 480 }}>
        <TableHead>
          <TableRow>
            <TableCell>
              Session
            </TableCell>
            <TableCell>
              {phrasing.status}
            </TableCell>
            <TableCell numeric>
              {phrasing.date}
            </TableCell>
            <TableCell numeric>
              {phrasing.hour}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {this.getFilteredSessions().map((session, i) => (
            <SessionTableRow
              onSessionClick={this.memoizeHandleSessionClick(session.id)}
              key={session.id}
              rowLabel={i + 1}
              session={session}
            />
          ))}
        </TableBody>
      </Table>
    )
  }

  private renderSessionSummary = () => {
    const phrasing = {
      achievements: <FormattedMessage id={'labels.achievements'} />,
      goals: <FormattedMessage id={'labels.goals'} />,
      close: <FormattedMessage id={'buttonActions.close'} />,
      sessionSummary: <FormattedMessage id={'generic.sessionSummary'} />,
    }

    const { isActive, deactivate } = this.props
    const { selectedSession } = this.state

    if (selectedSession === undefined) return null

    return (
      <Dialog
        fullWidth
        open={isActive}
        onClose={deactivate}
      >
        <DialogTitle>
          {phrasing.sessionSummary}
        </DialogTitle>
        <DialogContent>
          <Grid
            container
            spacing={16}
            direction={'column'}
          >
            <Grid item>
              <DialogContentText>
                {phrasing.goals}
              </DialogContentText>
              <Typography gutterBottom>
                {(selectedSession as Session).goals}
              </Typography>
            </Grid>
            <Grid item>
              <DialogContentText>
                {phrasing.achievements}
              </DialogContentText>
              <Typography gutterBottom>
                {(selectedSession as Session).achievements}
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <InjectAccountType>
            {({ accountType }) => (
              accountType == AccountType.Mentee ? <Button
                onClick={() => this.setState({
                  ...this.state,
                  editingSession: true
                })}
                color={'primary'}
                style={{textTransform: 'capitalize'}}
              >
                <FormattedMessage id={'buttonActions.edit'} />
              </Button> : <div></div>
            )}
          </InjectAccountType>
          <Button
            onClick={deactivate}
            color={'primary'}
            style={{textTransform: 'capitalize'}}
          >
            {phrasing.close}
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  handleChange = (value, param) => {
    switch (param) {
      case "goals":
        this.setState(s => ({
          goals: value
        }))
        return
      case "achievements":
        this.setState(s => ({
          achievements: value
        }))
        return
    }
  }

  saveSession = async () => {
    const { selectedSession } = this.state
    if (selectedSession) {
      try {
        await post(`/session?id=${(selectedSession! as Session).id}`, {
          goals: this.state.goals,
          achievements: this.state.achievements,
          date: this.state.date
        })
      } catch (e) {
        console.error(e)
      }
      this.props.refreshSessions()
      this.setState({ selectedSession: undefined, editingSession: false })
    }
  }

  private renderSessionEdit = () => {
    const phrasing = {
      achievements: <FormattedMessage id={'labels.achievements'} />,
      goals: <FormattedMessage id={'labels.goals'} />,
      close: <FormattedMessage id={'buttonActions.close'} />,
      sessionSummary: <FormattedMessage id={'generic.sessionSummary'} />,
    }

    const { isActive, deactivate } = this.props
    const { selectedSession } = this.state
    if (selectedSession === undefined) return null
    return (
      <Dialog
        fullWidth
        open={isActive}
        onClose={deactivate}
      >
        <DialogTitle>
          {phrasing.sessionSummary}
        </DialogTitle>
        <DialogContent>
          <div style={{
            display: 'flex'
          }}>
            <Grid
              container
              spacing={16}
              direction={'column'}
            >
              <Grid item>
                <DialogContentText>
                  {phrasing.goals}
                </DialogContentText>
                <Typography gutterBottom>
                  <TextField
                    type='text'
                    value={this.state.goals}
                    onChange={(e) => this.handleChange(e.currentTarget.value, 'goals')}
                  />
                </Typography>
              </Grid>
              <Grid item>
                <DialogContentText>
                  {phrasing.achievements}
                </DialogContentText>
                <Typography gutterBottom>
                  <TextField
                    type='text'
                    value={this.state.achievements}
                    onChange={(e) => this.handleChange(e.currentTarget.value, 'achievements')}
                  />
                </Typography>
              </Grid>
            </Grid>
            <div style={{ flex: 1, marginRight: 40 }}>
              <DateTimePicker
                value={null}
                onChange={(d) => {
                  console.log('DATE2', d.unix());
                  this.setState(s => ({
                    date: d.unix()
                  }))
                }}
                TextFieldComponent={(props: any) => {
                  console.log('DATE', this.state.date);
                  const d = new Date(this.state.date * 1000)
                  return (
                    <div {...props}>
                      <ListItem disableGutters>
                        <TodayIcon color={'primary'} />
                        <ListItemText primary={`${d.getDate()}/${d.getMonth()+1}/${d.getFullYear()}`} />
                      </ListItem>
                      <ListItem disableGutters>
                        <ScheduleIcon color={'primary'} />
                        <ListItemText primary={`${d.getHours()}:${d.getMinutes() > 9 ? d.getMinutes() : `0${d.getMinutes()}` }`} />
                      </ListItem>
                    </div>
                  )
                }}
              />
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button style={{textTransform: 'capitalize'}} color={'primary'} onClick={this.saveSession}>
            <FormattedMessage id='buttonActions.save' />
          </Button>
          <Button onClick={() => {
            this.setState({ editingSession: false })
            deactivate()
          }} color={'primary'}
          style={{textTransform: 'capitalize'}}>
            {phrasing.close}
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  private determineButtonVariant = status => {
    const { filter } = this.state

    return filter === status ? 'raised' : undefined
  }

  private memoizeHandleSessionClick = memoize(
    sessionId =>
      () => {
        this.setState(
          {
            selectedSession: this.props.sessions.find(
              ({ id }) => id === sessionId)
          },
          () => {
            const { selectedSession } = this.state
            if (selectedSession && (selectedSession! as Session).status === SessionStatus.Done) {
              this.props.activate()
              this.setState({
                achievements: (selectedSession! as Session).achievements || '',
                goals: (selectedSession! as Session).goals || '',
                date: (selectedSession! as Session).date! / 1000
              })
            }
          },
        )
      }
  )

  private memoizeSetFilter = memoize(
    filter =>
      () => {
        this.setFilter(filter)
      }
  )

  private removeFilter = () => {
    this.setFilter(undefined)
  }

  private getFilteredSessions = () => {
    const { filter } = this.state
    const { sessions } = this.props

    if (!filter) return sessions

    return sessions.filter(session => session.status === filter)
  }

  private setFilter = (filter?: SessionStatus) => {
    this.setState({ filter })
  }

  private updateNumSessions = async () => {
    const { programId, processId, update } = this.props

    await post('/process/sessions/add', {
      programId: parseInt(programId),
      processId: parseInt(processId)
    })

    setTimeout(() => {
      update()
    }, 500)
  }
}

export default loadable(withToggle(withStyles(styles)(Sessions)))



// WEBPACK FOOTER //
// ./src/components/molecules/Sessions.tsx